import { Order, OrderSortField } from '../models/Order';

export class OrderUtils {
  // Obtenir le statut en français
  static getStatusLabel(status: string): string {
    const statusLabels: Record<string, string> = {
      'PLACED': 'Commandé',
      'ACCEPTED': 'Accepté',
      'PREPARING': 'En préparation',
      'READY_FOR_PICKUP': 'Prêt pour récupération',
      'ACCEPTED_BY_DRIVER': 'Accepté par le livreur',
      'DELIVERING': 'En livraison',
      'DELIVERED': 'Livré',
      'CANCELLED': 'Annulé',
      'NO_DRIVER_FOUND': 'Aucun livreur trouvé',
      'PAYMENT_PENDING': 'Paiement en attente',
      'PAYMENT_FAILED': 'Paiement non finalisé'
    };
    return statusLabels[status] || status;
  }

  static getPaymentIssueLabel(order: Order): string {
    if (order.paymentFailureReasonLabel?.trim()) {
      return order.paymentFailureReasonLabel.trim();
    }

    switch ((order.paymentAttemptStatus || '').toUpperCase()) {
      case 'CUSTOMER_CANCELLED':
        return 'Paiement annulé par le client';
      case 'GATEWAY_CANCELLED':
        return 'Paiement annulé sur la page CMI';
      case 'BANK_DECLINED':
        return 'Paiement refusé par la banque ou CMI';
      case 'TECHNICAL_FAILED':
        return 'Erreur technique de paiement';
      case 'EXPIRED_NO_CALLBACK':
        return 'Paiement abandonné ou expiré';
      default:
        break;
    }

    switch ((order.cmiFailureReason || '').toUpperCase()) {
      case 'CUSTOMER_CANCELLED':
        return 'Paiement annulé par le client';
      case 'FRONT_FAIL':
        return 'Paiement annulé ou échoué sur la page CMI';
      case 'NO_CALLBACK_TIMEOUT':
        return 'Paiement abandonné ou expiré';
      case 'AMOUNT_MISMATCH':
        return 'Montant payé différent du total commande';
      default:
        return 'Paiement non finalisé';
    }
  }

  static getOrderStatusLabel(order: Order): string {
    if (order.status === 'PAYMENT_FAILED') {
      return this.getPaymentIssueLabel(order);
    }
    return this.getStatusLabel(order.status);
  }

  // Obtenir la couleur du statut
  static getStatusColor(status: string): string {
    const statusColors: Record<string, string> = {
      'PLACED': '#2196F3',
      'ACCEPTED': '#4CAF50',
      'PREPARING': '#FF9800',
      'READY_FOR_PICKUP': '#9C27B0',
      'ACCEPTED_BY_DRIVER': '#3F51B5',
      'DELIVERING': '#00BCD4',
      'DELIVERED': '#4CAF50',
      'CANCELLED': '#F44336',
      'NO_DRIVER_FOUND': '#F44336',
      'PAYMENT_PENDING': '#FF9800',
      'PAYMENT_FAILED': '#F57C00'
    };
    return statusColors[status] || '#757575';
  }

  static getOrderStatusColor(order: Order): string {
    if (order.status === 'PAYMENT_FAILED') {
      switch ((order.paymentAttemptStatus || '').toUpperCase()) {
        case 'BANK_DECLINED':
        case 'TECHNICAL_FAILED':
          return '#D32F2F';
        case 'EXPIRED_NO_CALLBACK':
          return '#6D4C41';
        case 'CUSTOMER_CANCELLED':
        case 'GATEWAY_CANCELLED':
          return '#F57C00';
        default:
          return '#F57C00';
      }
    }
    return this.getStatusColor(order.status);
  }

  // Obtenir le label de méthode de paiement
  static getPaymentMethodLabel(method: string): string {
    return method === 'cash' ? 'Espèces' : 'Carte bancaire';
  }

  // Obtenir le label de méthode de livraison
  static getDeliveryMethodLabel(method: string): string {
    return method === 'delivery' ? 'Livraison à domicile' : 'À emporter';
  }

  // Formater le montant en dirhams marocains
  static formatAmount(amount: number): string {
    return `${amount.toFixed(2)} MAD`;
  }

  // Obtenir le nom complet du driver
  static getDriverName(order: Order): string {
    if (!order.deliveryPerson) return 'Aucun driver';
    return `${order.deliveryPerson.firstName} ${order.deliveryPerson.lastName}`;
  }

  // Obtenir la description des produits
  static getProductsDescription(order: Order): string {
    if (!order.productNames || order.productNames.length === 0) {
      return `${order.items.length} produit(s)`;
    }
    return order.productNames.join(', ');
  }

  // Calculer le temps écoulé depuis la création
  static getTimeElapsed(createdAt: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - createdAt.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `Il y a ${diffDays} jour(s)`;
    } else if (diffHours > 0) {
      return `Il y a ${diffHours} heure(s)`;
    } else if (diffMinutes > 0) {
      return `Il y a ${diffMinutes} minute(s)`;
    } else {
      return 'À l\'instant';
    }
  }

  // Vérifier si un ordre est récent (moins de 1 heure)
  static isRecentOrder(createdAt: Date): boolean {
    const now = new Date();
    const diffMs = now.getTime() - createdAt.getTime();
    const diffMinutes = diffMs / (1000 * 60);
    return diffMinutes < 60;
  }

  // Vérifier si un ordre peut être modifié
  static canModifyOrder(order: Order): boolean {
    const nonModifiableStatuses = ['DELIVERING', 'DELIVERED', 'CANCELLED'];
    return !nonModifiableStatuses.includes(order.status);
  }

  // Vérifier si un ordre peut être annulé
  static canCancelOrder(order: Order): boolean {
    const nonCancellableStatuses = ['DELIVERING', 'DELIVERED', 'CANCELLED'];
    return !nonCancellableStatuses.includes(order.status);
  }

  // Obtenir les options de tri disponibles
  static getSortOptions(): Array<{ label: string; value: OrderSortField }> {
    return [
      { label: 'Date de création', value: 'createdAt' },
      { label: 'Nom du client', value: 'clientName' },
      { label: 'Téléphone du client', value: 'clientPhone' },
      { label: 'Produits commandés', value: 'products' },
      { label: 'Méthode de paiement', value: 'paymentMethod' },
      { label: 'Méthode de livraison', value: 'deliveryMethod' },
      { label: 'Nom du driver', value: 'driverName' },
      { label: 'Montant total', value: 'totalAmount' },
      { label: 'Statut', value: 'status' }
    ];
  }

  // Filtrer les ordres par terme de recherche
  static filterBySearchTerm(orders: Order[], searchTerm: string): Order[] {
    if (!searchTerm.trim()) return orders;

    const term = searchTerm.toLowerCase();
    return orders.filter(order =>
      order.id.toLowerCase().includes(term) ||
      order.clientName?.toLowerCase().includes(term) ||
      order.clientPhone?.toLowerCase().includes(term) ||
      order.storeName.toLowerCase().includes(term) ||
      this.getProductsDescription(order).toLowerCase().includes(term) ||
      this.getStatusLabel(order.status).toLowerCase().includes(term) ||
      this.getDriverName(order).toLowerCase().includes(term)
    );
  }

  // Grouper les ordres par statut
  static groupByStatus(orders: Order[]): Record<string, Order[]> {
    return orders.reduce((groups, order) => {
      const status = order.status;
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(order);
      return groups;
    }, {} as Record<string, Order[]>);
  }

  // Grouper les ordres par magasin
  static groupByStore(orders: Order[]): Record<string, Order[]> {
    return orders.reduce((groups, order) => {
      const storeId = order.storeId;
      if (!groups[storeId]) {
        groups[storeId] = [];
      }
      groups[storeId].push(order);
      return groups;
    }, {} as Record<string, Order[]>);
  }

  // Grouper les ordres par driver
  static groupByDriver(orders: Order[]): Record<string, Order[]> {
    return orders.reduce((groups, order) => {
      const driverId = order.deliveryPerson?.id || 'no-driver';
      if (!groups[driverId]) {
        groups[driverId] = [];
      }
      groups[driverId].push(order);
      return groups;
    }, {} as Record<string, Order[]>);
  }

  // Calculer les statistiques rapides d'une liste d'ordres
  static calculateQuickStats(orders: Order[]): {
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    cashOrders: number;
    cardOrders: number;
    deliveryOrders: number;
    pickupOrders: number;
  } {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + order.totalAmount, 0);
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    const cashOrders = orders.filter(order => order.paymentMethod === 'cash').length;
    const cardOrders = orders.filter(order => order.paymentMethod === 'card').length;
    const deliveryOrders = orders.filter(order => order.deliveryMethod === 'delivery').length;
    const pickupOrders = orders.filter(order => order.deliveryMethod === 'pickup').length;

    return {
      totalOrders,
      totalRevenue,
      averageOrderValue,
      cashOrders,
      cardOrders,
      deliveryOrders,
      pickupOrders
    };
  }

  // Obtenir les ordres par plage horaire
  static getOrdersByTimeRange(orders: Order[], startHour: number, endHour: number): Order[] {
    return orders.filter(order => {
      const hour = order.createdAt.getHours();
      return hour >= startHour && hour <= endHour;
    });
  }

  // Obtenir la distribution des ordres par heure
  static getHourlyDistribution(orders: Order[]): number[] {
    const distribution = new Array(24).fill(0);
    orders.forEach(order => {
      const hour = order.createdAt.getHours();
      distribution[hour]++;
    });
    return distribution;
  }

  // Obtenir la distribution des ordres par jour de la semaine
  static getDailyDistribution(orders: Order[]): number[] {
    const distribution = new Array(7).fill(0);
    orders.forEach(order => {
      const day = order.createdAt.getDay();
      distribution[day]++;
    });
    return distribution;
  }

  // Valider les IDs d'ordres pour la suppression
  static validateOrderIds(orderIds: string[]): { valid: string[]; invalid: string[] } {
    const valid: string[] = [];
    const invalid: string[] = [];

    orderIds.forEach(id => {
      if (typeof id === 'string' && id.trim().length > 0) {
        valid.push(id.trim());
      } else {
        invalid.push(id);
      }
    });

    return { valid, invalid };
  }

  // Créer un résumé d'ordre pour l'affichage
  static createOrderSummary(order: Order): {
    id: string;
    clientInfo: string;
    productsInfo: string;
    amountInfo: string;
    statusInfo: string;
    timeInfo: string;
  } {
    return {
      id: order.id,
      clientInfo: `${order.clientName || 'Client inconnu'} - ${order.clientPhone || 'N/A'}`,
      productsInfo: this.getProductsDescription(order),
      amountInfo: this.formatAmount(order.totalAmount),
      statusInfo: this.getStatusLabel(order.status),
      timeInfo: this.getTimeElapsed(order.createdAt)
    };
  }

  // Obtenir les ordres en retard (estimation dépassée)
  static getDelayedOrders(orders: Order[]): Order[] {
    const now = new Date();
    return orders.filter(order => {
      if (order.status === 'DELIVERED' || order.status === 'CANCELLED') {
        return false;
      }
      return order.estimatedEnd < now;
    });
  }

  // Calculer le temps de préparation moyen
  static getAveragePreparationTime(orders: Order[]): number {
    const completedOrders = orders.filter(order => 
      order.statusTimestamps.preparingAt && order.statusTimestamps.readyForPickupAt
    );

    if (completedOrders.length === 0) return 0;

    const totalTime = completedOrders.reduce((sum, order) => {
      const startTime = order.statusTimestamps.preparingAt!.getTime();
      const endTime = order.statusTimestamps.readyForPickupAt!.getTime();
      return sum + (endTime - startTime);
    }, 0);

    return totalTime / completedOrders.length / (1000 * 60); // en minutes
  }

  // Calculer le temps de livraison moyen
  static getAverageDeliveryTime(orders: Order[]): number {
    const deliveredOrders = orders.filter(order => 
      order.statusTimestamps.acceptedByDriverAt && 
      order.statusTimestamps.deliveredAt &&
      order.deliveryMethod === 'delivery'
    );

    if (deliveredOrders.length === 0) return 0;

    const totalTime = deliveredOrders.reduce((sum, order) => {
      const startTime = order.statusTimestamps.acceptedByDriverAt!.getTime();
      const endTime = order.statusTimestamps.deliveredAt!.getTime();
      return sum + (endTime - startTime);
    }, 0);

    return totalTime / deliveredOrders.length / (1000 * 60); // en minutes
  }

  // Infos d'annulation
  static getCancellationTimestamp(order: Order): Date | undefined {
    return order.cancellation?.cancelledAt || order.statusTimestamps.cancelledAt;
  }

  static getCancellationCause(order: Order): string {
    return order.cancellation?.reasonText || order.cancellation?.reasonCode || 'Raison non spécifiée';
  }

  // Obtenir les performances par driver
  static getDriverPerformance(orders: Order[]): Array<{
    driverId: string;
    driverName: string;
    totalDeliveries: number;
    averageDeliveryTime: number;
    totalEarnings: number;
  }> {
    const driverStats = new Map<string, {
      name: string;
      deliveries: Order[];
      totalEarnings: number;
    }>();

    orders
      .filter(order => order.deliveryPerson && order.status === 'DELIVERED')
      .forEach(order => {
        const driverId = order.deliveryPerson!.id;
        const driverName = this.getDriverName(order);

        if (!driverStats.has(driverId)) {
          driverStats.set(driverId, {
            name: driverName,
            deliveries: [],
            totalEarnings: 0
          });
        }

        const stats = driverStats.get(driverId)!;
        stats.deliveries.push(order);
        stats.totalEarnings += order.driverPayout;
      });

    return Array.from(driverStats.entries()).map(([driverId, stats]) => ({
      driverId,
      driverName: stats.name,
      totalDeliveries: stats.deliveries.length,
      averageDeliveryTime: this.getAverageDeliveryTime(stats.deliveries),
      totalEarnings: stats.totalEarnings
    }));
  }
}

export default OrderUtils;
